import {
  isAnalyticsEventName,
  type AnalyticsEventName,
  type AnalyticsEventPayloads,
} from "./events";

/**
 * Verzendlaag voor analytics. Er hangt bewust nog geen externe dienst aan: een
 * event gaat naar `window.dataLayer` — de neutrale aansluiting die elke
 * tagmanager later oppikt — en naar een bufferlijst die QA en Playwright kunnen
 * uitlezen. Wie een sink kiest, hoeft alleen dit bestand aan te passen.
 */

export type AnalyticsRecord = {
  event: AnalyticsEventName;
  page: string;
  /** De projectroute die de bezoeker al gekozen heeft, of `null`. */
  route: string | null;
  ts: number;
} & Record<string, unknown>;

/**
 * Dezelfde sleutel als `kwalificatieOpslagsleutel` in `_data/leadkwalificatie`.
 * Hier bewust letterlijk: de meetlaag mag niet omvallen als de projectrouter
 * verbouwd wordt.
 */
const kwalificatieSleutel = "evilla:leadkwalificatie:v1";

/** De route uit de projectrouter, zodat elk event weet in welke route het viel. */
function bekendeRoute(): string | null {
  try {
    const rauw = window.sessionStorage.getItem(kwalificatieSleutel);
    if (!rauw) return null;
    const route = (JSON.parse(rauw) as { route?: unknown }).route;
    return typeof route === "string" ? route : null;
  } catch {
    return null;
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    /** Alle events van deze paginasessie, op volgorde. Voor QA en tests. */
    __evillaAnalytics?: AnalyticsRecord[];
  }
}

function emit(name: AnalyticsEventName, payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;

  const record: AnalyticsRecord = {
    ...payload,
    event: name,
    page: window.location.pathname,
    route: bekendeRoute(),
    ts: Date.now(),
  };

  try {
    (window.dataLayer ??= []).push(record);
    (window.__evillaAnalytics ??= []).push(record);
    document.dispatchEvent(new CustomEvent("evilla:analytics", { detail: record }));

    if (process.env.NODE_ENV !== "production") {
      console.debug("[analytics]", name, record);
    }
  } catch {
    // Meten mag de site nooit stukmaken.
  }
}

/** De getypeerde route: gebruik deze vanuit componentcode. */
export function track<K extends AnalyticsEventName>(name: K, payload: AnalyticsEventPayloads[K]) {
  emit(name, payload as Record<string, unknown>);
}

/**
 * De DOM-route: gebruikt door de delegated clicklistener. Onbekende namen
 * worden genegeerd, zodat een typefout in de markup geen rommel in de data zet.
 */
export function trackFromDataset(name: string, payload: Record<string, string>) {
  if (!isAnalyticsEventName(name)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[analytics] onbekend event genegeerd: ${name}`);
    }
    return;
  }
  emit(name, payload);
}
